import React, { useRef, useState, useEffect } from 'react';
import { ResultCardProductProps } from "@usereactify/search";
import { useShopifyProduct } from "@app/scripts/react-hooks/shopify";

import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from "swiper/react";
import { chevronRight, chevronLeft, wishlistIcon } from "../../react-hooks/svgIcons";
import { shopUrl } from '../../react-hooks/Utilties';

export type ResultCardProductsProps = React.ComponentProps<
  NonNullable<ResultCardProductProps["render"]>
> & {
  onRender?: any
  instantSearch?: boolean;
  handlePageSizeStorage?: () => void;
};

export const ResultCardProduct: React.FC<ResultCardProductsProps> = ({
  onRender,
  instantSearch = false,
  handlePageSizeStorage,
  ...props
}) => {
  const {
    product,
    itemRef,
    handleClick,
    price,
    compareAtPrice,
    onSale
  } = props;

  const { highlight, badges, swatchImages, siblingsData } = useShopifyProduct({ product });

  const [currentProduct, setCurrentProduct] = useState(product);
  const [arrowsDisplay, setArrowsDisplay] = useState(false);
  const [finalSwatchData, setFinalSwatchData] = useState([]);
  const [currentProductMedia, setCurrentProductMedia] = useState([]);

  const productVariantHandler = (handle: string) => {
    if (handle) {
      const selectedVariant = siblingsData?.find((variant) => variant?.handle === handle);
      setCurrentProduct(selectedVariant);
    }
  };

  // Sorts swatches so that the current product swatch is always shown first
  const swatchDataSort = (swatchData: any, currentProduct: any) => {
    return swatchData.sort((swatch: any) =>
      swatch.handle === currentProduct.handle ? -1 : 1
    );
  };

  useEffect(() => {
    if (onRender) {
      onRender();
    }
  }, [onRender]);

  useEffect(() => {
    if (currentProduct?.media) {
      setCurrentProductMedia(currentProduct?.media)
    } else {
      setCurrentProductMedia(currentProduct?.images)
    }

  }, [currentProduct]);

  useEffect(() => {
    if (swatchImages) {
      const sortedSwatchData = swatchDataSort(swatchImages, currentProduct);
      setFinalSwatchData(sortedSwatchData);
    }
  }, [swatchImages]);

  const swiperRef = useRef(null);
  const id = `item-carousel-${product?._index}`;

  return (
    <div ref={itemRef} className="rs__result-card-product relative" onClick={handlePageSizeStorage}>
      <div
        onMouseEnter={() => setArrowsDisplay(true)}
        onMouseLeave={() => setArrowsDisplay(false)}
      >
        {!instantSearch ? (<div className="collection-product__badge flex flex-wrap absolute">
          {badges.length > 0 &&
            badges.map((badge, index) => (
              <span
                key={index}
                className={`collection-product__badge-item flex align-center uppercase ${badge.includes('%') ? "badge--on-sale" : ""
                  }`}
              >
                {badge}
              </span>
            ))}
        </div>) : null}
        {!instantSearch && (
          <button
            className={`product-card__wishlist-star swym-button swym-add-to-wishlist-view-product product_${product.id}`}
            data-with-epi="true"
            data-swaction="addToWishlist"
            data-product-id={`${product.id}`}
            data-variant-id={`${product.variants[0].id}`}
            data-product-url={`https://${shopUrl}${product.url}`}
          >
            {wishlistIcon}
          </button>
        )}

        {instantSearch ? (
          currentProduct?.image && (
            <a
              className="rs__result-card-product__link"
              onClick={handleClick}
              href={`/products/${currentProduct.handle}`}
            >
              <img
                className="rs__result-card-product__image--mobile w-full"
                srcSet={`
                  ${currentProduct?.image || currentProduct?.image}&width=200 200w,
                  ${currentProduct?.image || currentProduct?.image}&width=400 400w
                `}
                sizes="(max-width: 768px) 300vw, 50vw"
                src={`${currentProduct?.image || currentProduct?.image}&width=400`}
                alt={"Product Image"}
              />
            </a>
          )
        ) : (
          <Swiper
            id={id}
            ref={swiperRef}
            className="relative"
            loop={false}
            modules={[Navigation, Pagination, Scrollbar, A11y]}
            draggable
            navigation={{
              nextEl: `#${id} .product-card__next-btn`,
              prevEl: `#${id} .product-card__prev-btn`,
            }}
            scrollbar={{
              el: `#${id} .swiper-scrollbar`,
              draggable: true,
            }}
            pagination={{
              el: `#product-media-swiper-pagination`,
              clickable: true,
              renderBullet: (index, className) => `<div class="${className}"></div>`,
            }}
            spaceBetween={0}
            slidesPerView={1}
          >
            {currentProductMedia.map((media, index) => (
              <SwiperSlide key={index}>
                <a
                  className="rs__result-card-product__link"
                  onClick={handleClick}
                  href={`/products/${currentProduct.handle}`}
                >
                  {media?.type === "VIDEO" ? (
                    <video
                      className="rs__result-card-product__image w-full"
                      muted
                      loop
                      autoPlay
                      playsInline
                      webkit-playsinline="true"
                    >
                      <source src={media?.src} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img
                      className="rs__result-card-product__image w-full"
                      loading={index === 0 ? 'eager' : 'lazy'}
                      srcSet={`
                      ${media?.src || media?.url}&width=200 200w,
                      ${media?.src || media?.url}&width=400 400w,
                      ${media?.src || media?.url}&width=700 700w
                    `}
                      sizes="(max-width: 768px) 300vw, 50vw"
                      src={`${media?.src || media?.src}&width=400`}
                      alt={media?.alt || "Product Image"}
                    />
                  )
                  }
                </a>
              </SwiperSlide>
            ))}

            <div
              className={`product-card__next-btn button--carousel absolute hidden ${arrowsDisplay ? 'sm:block' : ''}`}
            >
              <span>{chevronLeft}</span>
            </div>
            <div
              className={`product-card__prev-btn button--carousel absolute hidden ${arrowsDisplay ? 'sm:block' : ''}`}
            >
              <span>{chevronRight}</span>
            </div>

            <div className="swiper-scrollbar sm:hidden swiper-scrollbar-horizontal"></div>
          </Swiper>
        )}
      </div>


      <div className="collection-product__details flex flex-col">
        <a
          className="rs__result-card-product__link"
          onClick={handleClick}
          href={`/products/${currentProduct?.handle}`}
        >
          <p className="rs__result-card-product__title">
            {currentProduct?.title}
          </p>
        </a>
        <div className="collection__price-container flex">
          {onSale && (
            <span
              className="rs__result-card-product__price-sale text-subdued bold"
              style={{ textDecoration: "line-through" }}
            >
              {compareAtPrice?.s?.symbol}{compareAtPrice?.value}
            </span>
          )}
          <span className={`rs__result-card-product__price ${onSale && 'rs__result-card-product__price-sale-text'}`}>
            {price?.s?.symbol}{price?.value}
          </span>
        </div>
      </div>
      <div className="rs__result-card__swatches flex">
        {finalSwatchData.length > 0 && !instantSearch &&
          finalSwatchData
            .filter((image) => image?.available)
            .map((image, index) => (
              <div
                className="rs__result-card-product__swatch-image-container"
                onClick={() => productVariantHandler(image?.handle)}
                key={index}
              >
                <img
                  className={`rs__result-card-product__swatch-image w-full ${image?.handle === currentProduct?.handle &&
                    "rs__result-card-product__swatch-image--selected"
                    }`}
                  src={image?.image}
                  alt={`Swatch ${index}`}
                  onError={(e) => {
                    if (image?.backup_image) {
                      e.target.src = image.backup_image;
                    } else {
                      e.target.style.display = "none";
                    }
                  }}
                />
              </div>
            ))}
      </div>

      {highlight && !instantSearch && (
        <p className="collection-product__highlight bold uppercase">
          {highlight}
        </p>
      )}
    </div>
  );
};
